import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { DSSetup, RequestError, Service } from 'webapp-types';
import { GranulationTypeId, MetricDetailsTab, TimeUnit } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { copyObject, copyToClipboard } from 'webapp/utils/common';

import didUpdate from 'webapp/modifiers/did-update';
import MetricDetailsExploreComponent from 'webapp/components/metric-details/explore';
import MetricDetailsForecastsExplorerComponent from 'webapp/components/metric-details/forecasts/explorer';
import MetricDetailsMetricSelectorComponent from 'webapp/components/metric-details/metric-selector';
import MetricDetailsTabsComponent from 'webapp/components/metric-details/tabs';

import type { Forecast } from 'webapp/models-custom';
import DateRange from 'webapp/models-custom/date-range';
import MetricSetup from 'webapp/models-custom/metric-setup';

import type MetricDetailsService from 'webapp/services/metric-details';
import type {
	ExploreTabState,
	ForecastTabState,
	NormalizedDetailState,
	SharedMetricDetailsState,
} from 'webapp/services/metric-details';

function getTabTrackingOrigin(tab: MetricDetailsTab): string {
	return tab === MetricDetailsTab.FORECAST ? 'forecast' : 'metrics_detail';
}

interface Args {
	initialState?: NormalizedDetailState | null;
	onClose?: () => void;
}

const EMPTY_SHARED_STATE: SharedMetricDetailsState = {
	metricSetup: { key: '', spaceAccessId: null },
};

const EMPTY_EXPLORE_STATE: ExploreTabState = {
	dateRange: null,
	visualization: null,
	anomalyEnabled: null,
	anomalyConfig: null,
};

export default class MetricDetailsComponent extends Component<Args> {
	@service('metric-details')
	declare metricDetailsService: MetricDetailsService;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;

	// shared state (metric setup data shared between explore and forecast tabs)
	@tracked sharedState: SharedMetricDetailsState = this.getEmptySharedState();
	@tracked currentExploreState: ExploreTabState = { ...EMPTY_EXPLORE_STATE };
	@tracked currentForecastState: ForecastTabState | null = null;
	@tracked disabledTooltipText?: string;
	// active tab state
	@tracked activeTab: MetricDetailsTab = MetricDetailsTab.EXPLORE;
	@tracked private isRemountingTab = false; // hides the tab for a runloop, to re-create its component

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.onInitialStateChange();

		if (!this.currentExploreState.dateRange) {
			this.currentExploreState = {
				...this.currentExploreState,
				dateRange: DateRange.createLastX(30, TimeUnit.DAY, GranulationTypeId.DAILY).formula,
			};
		}
	}

	get emptyGuidanceText(): string {
		if (this.isForecastTab) {
			return 'Start by selecting a metric to calculate the forecast';
		}
		return 'Choose a metric';
	}

	get isExploreTab(): boolean {
		return !this.isRemountingTab && this.activeTab === MetricDetailsTab.EXPLORE;
	}

	get isForecastTab(): boolean {
		return !this.isRemountingTab && this.activeTab === MetricDetailsTab.FORECAST;
	}

	hydrateFromState(state: NormalizedDetailState): void {
		this.sharedState = { ...state.shared };

		if (state.explore) {
			this.currentExploreState = { ...state.explore };
		}

		if (state.forecast) {
			this.currentForecastState = state.forecast;
		}
	}

	@action
	onInitialStateChange(): void {
		if (this.args.initialState) {
			this.applyState(this.args.initialState);
		}
	}

	private applyState(state: NormalizedDetailState): void {
		const effectiveState = this.validateAndMaybeResetState(state);
		const targetTab = effectiveState.tab || MetricDetailsTab.EXPLORE;
		this.hydrateFromState(effectiveState);

		if (targetTab === this.activeTab) {
			// force component re-creation when switching to the same tab
			// (e.g. loading a different saved forecast while already on forecast tab)
			this.isRemountingTab = true;
			next(this, () => {
				this.isRemountingTab = false;
			});
		} else {
			this.activeTab = targetTab;
		}
	}

	private validateAndMaybeResetState(state: NormalizedDetailState): NormalizedDetailState {
		const key = state.shared?.metricSetup?.key;
		if (!key) {
			if (this.metricDetailsService.isMobileMode) {
				this.closeWithError('Metric not found');
			}
			return state;
		}

		const metricSetup = new MetricSetup();
		metricSetup.setFromMetricSetupData(state.shared.metricSetup);

		const metric = metricSetup.metric;
		const isLockedFeature = metric?.showQbWarning || metric?.showScriptWarning;

		if (metric && !isLockedFeature) {
			return state;
		}

		const errorText = isLockedFeature
			? "Can't use this metric on current plan"
			: 'Metric not found';

		if (this.metricDetailsService.isMobileMode) {
			this.closeWithError(errorText);
			return state;
		}

		// the empty state then picks a default metric on explore, or shows the metric picker on forecast
		this.infolayerService.error(errorText);

		return {
			...state,
			shared: this.getEmptySharedState(),
		};
	}

	// The mobile drawer has no metric picker to fall back on.
	private closeWithError(text: string): void {
		this.infolayerService.error(text);
		// this runs while rendering
		next(() => this.args.onClose?.());
	}

	@action
	switchTab(tab: MetricDetailsTab): void {
		if (tab === MetricDetailsTab.EXPLORE) {
			this.disabledTooltipText = undefined;
		}

		if (tab !== this.activeTab) {
			this.popupService.closeOnlyContextPopups();

			const metric = this.sharedState.metricSetup?.key
				? new MetricSetup(this.sharedState.metricSetup).metric
				: null;

			if (metric) {
				if (tab === MetricDetailsTab.FORECAST) {
					this.analyticsService.event(TrackingEvent.FORECAST_DETAILS_VIEWED, {
						metrics: metric.name,
						datasource: metric.dataSourceTypeKey,
					});
				} else if (tab === MetricDetailsTab.EXPLORE) {
					this.metricDetailsService.trackMetricInsightsViewed('metrics_detail', metric);
				}
			}
		}

		this.activeTab = tab;
	}

	@action
	updateExploreState(state: ExploreTabState): void {
		this.currentExploreState = { ...state };
	}

	@action
	copyShareableLink(): void {
		const state = this.buildNormalizedState();
		const url = this.metricDetailsService.buildShareableUrl(state);

		copyToClipboard(url);
		this.infolayerService.ok('Link copied to clipboard');

		this.analyticsService.event(TrackingEvent.METRIC_DETAILS_URL_COPIED, {
			origin: getTabTrackingOrigin(this.activeTab),
		});
	}

	@action
	openMetricSelectionPopup(): void {
		if (this.disabledTooltipText) {
			return;
		}

		const currentSetup = this.sharedState.metricSetup;

		this.mmDataSelectsService
			.openMetricSelectWithAttributes({
				metricSetupData: {
					key: currentSetup?.key || '',
					spaceAccessId: currentSetup?.spaceAccessId || null,
				},
				skipAdditionalProperties: true,
				metricSelectOptions: {
					skipGoals: true,
				},
			})
			.then((metricSetupData: DSSetup.MetricSetupData) => {
				this.handleMetricSetupChange(metricSetupData);
			})
			.catch(() => {
				// user closed popup — no action needed
			});
	}

	@action
	handleMetricSetupChange(data: DSSetup.MetricSetupData): void {
		const currentMetricSetup = new MetricSetup(this.sharedState.metricSetup);
		const newMetricSetup = new MetricSetup(data);
		const metricSetupChanged = !currentMetricSetup.isMatchingMetricSetup(newMetricSetup);

		this.sharedState = { metricSetup: { ...data } };

		if (metricSetupChanged) {
			this.currentForecastState = null;
			this.disabledTooltipText = undefined;
		}
	}

	@action
	updateSharedMetricDetailsState(data: SharedMetricDetailsState): void {
		const currentMetricSetup = new MetricSetup(this.sharedState.metricSetup),
			newMetricSetup = new MetricSetup(data.metricSetup),
			metricSetupChanged = !currentMetricSetup.isMatchingMetricSetup(newMetricSetup);

		this.sharedState = { ...data };

		if (metricSetupChanged) {
			this.currentForecastState = null;
		}
	}

	@action
	handleDisabledTooltipTextChange(text: string | undefined): void {
		this.disabledTooltipText = text;
	}

	@action
	updateForecastState(state: ForecastTabState): void {
		this.currentForecastState = state;
	}

	@action
	async handleSaveForecast(forecast: Forecast): Promise<void> {
		try {
			await forecast.save();
		} catch (errorData) {
			this.requestService.showAllValidationErrors(errorData as RequestError);
			throw errorData;
		}
	}

	@action
	async handleDeleteForecast(forecast: Forecast): Promise<void> {
		forecast.trackEvent(TrackingEvent.FORECAST_REMOVED);
		await forecast.deleteAndCatch();

		this.sharedState = this.getEmptySharedState();
		this.currentForecastState = null;
	}

	private getEmptySharedState(): SharedMetricDetailsState {
		return copyObject(EMPTY_SHARED_STATE);
	}

	private buildNormalizedState(): NormalizedDetailState {
		const state: NormalizedDetailState = {
			shared: { ...this.sharedState },
			tab: this.activeTab,
		};

		if (this.activeTab === MetricDetailsTab.EXPLORE) {
			state.explore = { ...this.currentExploreState };
		} else if (this.currentForecastState) {
			state.forecast = this.currentForecastState;
		}

		return state;
	}

	<template>
		<div class="metric-explorer" {{didUpdate this.onInitialStateChange @initialState}}>
			{{#if this.isExploreTab}}
				<MetricDetailsExploreComponent
					@sharedState={{this.sharedState}}
					@initialTabState={{this.currentExploreState}}
					@onTabStateChange={{this.updateExploreState}}
					@onSharedStateChange={{this.updateSharedMetricDetailsState}}
					@onShare={{this.copyShareableLink}}
					@onClose={{@onClose}}
				>
					<:header>
						<div class="metric-details__header">
							<MetricDetailsTabsComponent
								@activeTab={{this.activeTab}}
								@switchTab={{this.switchTab}}
							/>
							<MetricDetailsMetricSelectorComponent
								@metricSetupData={{this.sharedState.metricSetup}}
								@onMetricSelect={{this.openMetricSelectionPopup}}
								@emptyGuidanceText={{this.emptyGuidanceText}}
							/>
						</div>
					</:header>
				</MetricDetailsExploreComponent>
			{{else if this.isForecastTab}}
				<MetricDetailsForecastsExplorerComponent
					@model={{null}}
					@sharedState={{this.sharedState}}
					@initialTabState={{this.currentForecastState}}
					@onSharedStateChange={{this.updateSharedMetricDetailsState}}
					@onTabStateChange={{this.updateForecastState}}
					@onSaveForecast={{this.handleSaveForecast}}
					@onDeleteForecast={{this.handleDeleteForecast}}
					@onDisabledTooltipTextChange={{this.handleDisabledTooltipTextChange}}
					@onShare={{this.copyShareableLink}}
					@onClose={{@onClose}}
				>
					<:header>
						<div class="metric-details__header">
							<MetricDetailsTabsComponent
								@activeTab={{this.activeTab}}
								@switchTab={{this.switchTab}}
							/>
							<MetricDetailsMetricSelectorComponent
								@metricSetupData={{this.sharedState.metricSetup}}
								@onMetricSelect={{this.openMetricSelectionPopup}}
								@disabledTooltipText={{this.disabledTooltipText}}
								@emptyGuidanceText={{this.emptyGuidanceText}}
							/>
						</div>
					</:header>
				</MetricDetailsForecastsExplorerComponent>
			{{/if}}
		</div>
	</template>
}
